import get from 'lodash/get';
import { h, defineComponent, computed } from 'vue';
import { productMetaFieldTransformer } from '@/components/shop/products/Product';
import { addVariantToCart, fetchAddOnProduct } from '@/utils/product';
import { useCartStore } from '@/stores/cart';

export default defineComponent({
  name: 'ProductProvider',

  props: {
    handle: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const cartStore = useCartStore();
    const { $shopifyProductByHandle } = useNuxtApp();

    // Fetch product data and add-on if metafield exists
    const { data: productData } = useAsyncData(
      `product-${props.handle}`,
      async () => {
        // Skip request if handle is 'null'
        if (props.handle === 'null') {
          return { product: null, addOn: null };
        }

        try {
          // Fetch product data
          const productRequest = await $shopifyProductByHandle(props.handle);
          const product = productMetaFieldTransformer(productRequest);

          // Fetch add-on product if digitalAddOnHandle exists
          const addOn = await fetchAddOnProduct(product, $shopifyProductByHandle);

          return { product, addOn };
        } catch (error) {
          console.error('Error fetching product:', error);
          return { product: null, addOn: null, error };
        }
      },
      {
        // Cache product data to prevent unnecessary refetching
        watch: [() => props.handle],
      }
    );

    // Create computed properties for the component
    const product = computed(() => productData.value?.product || null);
    const error = computed(() => productData.value?.error || null);
    const addOn = computed(() => productData.value?.addOn || null);

    const firstVariant = computed(() => {
      return product.value?.variants?.edges[0]?.node;
    });

    const addToCartEnabled = computed(() => {
      return product.value?.availableForSale;
    });

    const addToCartDisabled = computed(() => {
      return !addToCartEnabled.value;
    });

    const price = computed(() => {
      return firstVariant.value?.price || null;
    });

    const compareAtPrice = computed(() => {
      return firstVariant.value?.compareAtPrice || null;
    });

    const priceRangeMin = computed(() => {
      return get(product.value, 'priceRange.minVariantPrice.amount', null);
    });

    const priceRangeMax = computed(() => {
      return get(product.value, 'priceRange.maxVariantPrice.amount', null);
    });

    const title = computed(() => {
      return product.value?.title;
    });

    const isCourse = computed(() => {
      return product.value?.digitalProductType?.digitalProductType === 'Online Course';
    });

    // Get line items from the cart store
    const lineItems = computed(() => {
      return cartStore.lineItems;
    });

    // Method to add product to cart
    const addToCart = () => {
      if (!addToCartEnabled.value) return;

      // Call the addVariantToCart utility with the correct context
      addVariantToCart.call(
        { cartStore, $dlPush: useNuxtApp().$dlPush, lineItems: lineItems.value },
        product.value,
        firstVariant.value,
        isCourse.value,
        1,
        addOn.value
      );
    };

    // Return everything needed for the render function
    return {
      product,
      error,
      addToCartEnabled,
      addToCartDisabled,
      addToCart,
      firstVariant,
      price,
      compareAtPrice,
      priceRangeMin,
      priceRangeMax,
      title,
      isCourse,
      lineItems,
      addOn,
    };
  },

  render() {
    // Prepare the slot props
    const slotProps = {
      product: this.product,
      error: this.error,
      addToCartEnabled: this.addToCartEnabled,
      addToCartDisabled: this.addToCartDisabled,
      addToCart: this.addToCart,
      firstVariant: this.firstVariant,
      price: this.price,
      compareAtPrice: this.compareAtPrice,
      priceRangeMin: this.priceRangeMin,
      priceRangeMax: this.priceRangeMax,
      title: this.title,
      isCourse: this.isCourse,
      addOn: this.addOn,
    };

    // Create the default slot with the props
    const slots = this.$slots.default?.(slotProps) || [];

    // Render the component
    return h('div', {}, slots);
  },
});
